import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaUserGraduate,
  FaEnvelope,
  FaChalkboardTeacher,
  FaStar,
  FaBookOpen,
  FaThumbsUp,
  FaPaperPlane,
} from "react-icons/fa";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Rating,
} from "@mui/material";
import { getInstructorWithCourses } from "../../State/Instructor/Action";
import AppPagination from "../Tools/AppPagination";
import { addInstructorReview } from "../../State/Review/Action";
import ErrorDialog from "../Tools/ErrorDialog";
import InstructorReviews from "./InstructorReviews";

const InstructorWithCourses = () => {
  const dispatch = useDispatch();
  const { instructorId } = useParams();
  const [page, setPage] = useState(0);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [ratingValue, setRatingValue] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const instructorData = useSelector(
    (state) => state.instructor?.instructorWithCourses?.data
  );

  const totalPages =
    useSelector(
      (state) => state.instructor?.instructorWithCourses?.data?.totalPages
    ) || 0;

  const instructorReviewError = useSelector(
    (state) => state.review?.instructorReviewError
  );

  const instructorReviewResult = useSelector(
    (state) => state.review?.instructorReviewResult
  );

  useEffect(() => {
    if (instructorId) {
      dispatch(getInstructorWithCourses(instructorId, page));
    }
  }, [dispatch, instructorId, page, instructorReviewResult]);

  useEffect(() => {
    if (instructorReviewError) {
      setShowErrorDialog(true);
    }
  }, [instructorReviewError]);

  if (!instructorData) return null;

  const {
    name,
    headline,
    bio,
    profileImageUrl,
    courses,
    rating,
    totalReviews,
    hasUserRated,
  } = instructorData;

  const handleRateInstructor = () => {
    setRatingDialogOpen(true);
  };

  const handleDialogClose = () => {
    setRatingDialogOpen(false);
    setRatingValue(0);
    setReviewText("");
  };

  const handleSubmitReview = () => {
    const reviewData = {
      rating: ratingValue,
      reviewText: reviewText,
    };
    dispatch(addInstructorReview(instructorId, reviewData));
    handleDialogClose();
  };

  const handleOpenReviewDialog = () => {
    setReviewDialogOpen(true);
  };

  const handleCloseReviewDialog = () => {
    setReviewDialogOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-yellow-50 to-blue-100 py-10 px-4">
      <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-xl p-6 md:p-10">
        <div className="flex flex-col md:flex-row items-center gap-6 relative">
          <img
            src={profileImageUrl}
            alt={name}
            className="w-32 h-32 rounded-full shadow-md object-cover border-4 border-blue-200"
          />
          <div className="flex-1">
            <h1 className="text-3xl font-extrabold text-indigo-700 flex items-center gap-3">
              <FaChalkboardTeacher className="text-blue-500" /> {name}
              <span
                className="flex items-center gap-1 text-sm font-medium bg-yellow-50 text-yellow-600 px-3 py-1 rounded-full shadow-sm cursor-pointer"
                onClick={handleOpenReviewDialog}
              >
                <FaStar className="text-yellow-400" />
                {rating.toFixed(1)}
                <span className="text-sm text-gray-500 ml-1">
                  ({totalReviews} reviews)
                </span>
              </span>
            </h1>

            <p className="text-md mt-2 text-gray-600 italic flex items-center gap-2">
              <FaUserGraduate className="text-yellow-500" /> {headline}
            </p>

            <p className="text-gray-700 mt-4 flex items-center gap-2">
              <FaEnvelope className="text-red-500" /> {bio}
            </p>
          </div>

          <button
            onClick={handleRateInstructor}
            disabled={hasUserRated}
            className={`absolute top-0 right-0 mt-2 mr-2 inline-flex items-center gap-2 
              ${
                hasUserRated
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-indigo-500 hover:bg-indigo-600"
              } 
              text-white text-sm font-semibold px-4 py-2 rounded-full shadow-md transition duration-300`}
          >
            <FaThumbsUp /> {hasUserRated ? "Already Rated" : "Rate Instructor"}
          </button>
        </div>

        <h2 className="text-2xl font-bold text-indigo-800 mt-10 mb-6 flex items-center gap-2">
          <FaBookOpen className="text-green-500" /> Courses by {name}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {courses?.map((course, index) => (
            <motion.div
              key={course.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition duration-300"
            >
              <img
                src={course.thumbnailUrl}
                alt={course.name}
                className="w-full h-24 object-cover rounded-md mb-3"
              />
              <h3 className="text-md font-semibold text-indigo-700 flex items-center gap-1">
                <FaBookOpen className="text-blue-400" /> {course.name}
              </h3>
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                <FaStar className="text-yellow-400" />{" "}
                {course.averageRating.toFixed(1)} ⭐ ({course.totalReviews}{" "}
                reviews)
              </p>
            </motion.div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="mt-12 flex justify-center">
            <AppPagination setPage={setPage} totalPages={totalPages} />
          </div>
        )}
      </div>

      {/* Instructor Rating Dialog */}
      <Dialog
        open={ratingDialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className="bg-indigo-100 text-indigo-900 font-bold">
          Rate Instructor: {name}
        </DialogTitle>
        <DialogContent className="bg-white space-y-4 py-6">
          <div className="flex justify-center">
            <Rating
              name="instructor-rating"
              value={ratingValue}
              onChange={(e, newValue) => setRatingValue(newValue)}
              size="large"
            />
          </div>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Write your instructor review"
            variant="outlined"
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
          />
        </DialogContent>
        <DialogActions className="bg-indigo-50 py-4 px-6">
          <Button onClick={handleDialogClose} variant="outlined" color="error">
            Cancel
          </Button>
          <Button
            onClick={handleSubmitReview}
            variant="contained"
            color="primary"
            disabled={ratingValue === 0 || reviewText.trim() === ""}
            startIcon={<FaPaperPlane />}
          >
            Submit Review
          </Button>
        </DialogActions>
      </Dialog>

      {/* Instructor Reviews Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={handleCloseReviewDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle className="bg-indigo-100 text-indigo-900 font-bold relative flex justify-between items-center">
          <span>All Reviews for {name}</span>
          <Button
            onClick={handleCloseReviewDialog}
            sx={{ minWidth: "auto", padding: 0 }}
          >
            <span className="text-lg text-red-500 hover:text-red-700 font-bold px-2">
              ✕
            </span>
          </Button>
        </DialogTitle>
        <DialogContent className="bg-white">
          <InstructorReviews instructorId={instructorId} />
        </DialogContent>
      </Dialog>

      <ErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        errorMessage={instructorReviewError?.message}
      />
    </div>
  );
};

export default InstructorWithCourses;
